import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { App as CapApp } from '@capacitor/app';
import { api, auth } from './api';
import { androidInfo, isAndroidApp } from './androidApp';

/**
 * Pushnotiser i iPhone-appen (N1) och Android-appen (AN1). API:t skickar via APNs respektive
 * FCM (apps/api/src/lib/push.ts); plattformen följer med registreringen.
 *
 * - Tillstånd frågas inte vid första start, utan när notiser gör nytta: man går med i en
 *   mark, startar eller ansluter till en jakt, planerar en jakt, svarar på en inbjudan, köper
 *   en jakt — eller slår på dem i profilen (askForPush).
 * - Har man redan sagt ja registreras telefonen vid varje start (token kan bytas).
 * - Öppen chatt rapporteras till API:t (setOpenChat), som då inte pushar den chatten hit.
 * - Tryck på en notis öppnar rätt vy (händelsen qvenari:push-open, lyssnas på i App.tsx).
 *
 * Webben laddas från qvenari.com, så äldre appbyggen utan pluginet får samma kod —
 * allt här är tyst utan pluginet.
 */

/** Vad notisen ska öppna; samma fält som PushTarget i API:t. */
export interface PushTarget {
  kind?: string;
  view: 'mark' | 'hunt' | 'invite' | 'purchases';
  markId?: string;
  tab?: string;
  meetingId?: string;
  token?: string;
}

export const PUSH_OPEN_EVENT = 'qvenari:push-open';
/** App.tsx → VMarketplace: öppna en mark på en viss flik ({ markId, tab, huntChat }). */
export const OPEN_MARK_TAB_EVENT = 'qvenari:open-mark-tab';
/** VMarketplace → KartaTab: visa jaktchatten i den pågående jakten. */
export const OPEN_HUNT_CHAT_EVENT = 'qvenari:open-hunt-chat';
const TOKEN_KEY = 'qvenari_push_token';
const VIEWS = ['mark', 'hunt', 'invite', 'purchases'];

export function isPushAvailable(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');
  } catch {
    return false;
  }
}

/**
 * Android-byggen utan google-services.json kan inte registrera sig (pluginet kraschar appen),
 * så där är notiser avstängda tills Firebase är uppsatt.
 */
async function pushUsable(): Promise<boolean> {
  if (!isPushAvailable()) return false;
  if (!isAndroidApp()) return true;
  return (await androidInfo()).fcm;
}

/** Notisens data (APNs userInfo, FCM data) → vad som ska öppnas. Okänt innehåll ger null. */
export function pushTargetFromData(data: unknown): PushTarget | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const view = typeof d.view === 'string' && VIEWS.includes(d.view) ? d.view as PushTarget['view'] : null;
  if (!view) return null;
  const str = (k: string) => (typeof d[k] === 'string' && d[k] ? String(d[k]) : undefined);
  const t: PushTarget = { view, kind: str('kind'), markId: str('markId'), tab: str('tab'), meetingId: str('meetingId'), token: str('token') };
  if (view === 'mark' && !t.markId) return null;
  if (view === 'hunt' && (!t.markId || !t.meetingId)) return null;
  if (view === 'invite' && !t.token) return null;
  return t;
}

export type OpenAction =
  | { type: 'navigate'; url: string }
  | { type: 'markTab'; markId: string; tab: string; huntChat?: boolean }
  | { type: 'purchases' };

/**
 * Hur appen öppnar målet. En jakt man redan är med i öppnas på kartan med jaktchatten;
 * annars ansluter man via samma länk som i gruppchatten (?join=…&mark=…).
 */
export function openActionFor(t: PushTarget, activeHuntMeetingId: string | null): OpenAction {
  switch (t.view) {
    case 'invite': return { type: 'navigate', url: `/inbjudan/${encodeURIComponent(t.token!)}` };
    case 'purchases': return { type: 'purchases' };
    case 'hunt':
      if (activeHuntMeetingId && activeHuntMeetingId === t.meetingId) return { type: 'markTab', markId: t.markId!, tab: 'karta', huntChat: t.kind === 'chat' };
      return { type: 'navigate', url: `/?join=${encodeURIComponent(t.meetingId!)}&mark=${encodeURIComponent(t.markId!)}` };
    default: return { type: 'markTab', markId: t.markId!, tab: t.tab || 'karta' };
  }
}

function storedToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function storeToken(token: string | null): void {
  try { if (token) localStorage.setItem(TOKEN_KEY, token); else localStorage.removeItem(TOKEN_KEY); } catch { /* privat läge */ }
}

let listeners: PluginListenerHandle[] = [];
let pendingToken: string | null = null;
const loggedIn = () => { try { return auth.isLoggedIn(); } catch { return false; } };

async function sendToken(token: string): Promise<void> {
  if (!loggedIn()) { pendingToken = token; return; }
  pendingToken = null;
  try {
    await api.registerPushDevice(token, Capacitor.getPlatform());
    storeToken(token);
    void reportPresence();
  } catch (e) {
    console.warn('[push] kunde inte registrera telefonen', e);
  }
}

async function addListeners(): Promise<void> {
  if (listeners.length) return;
  listeners = await Promise.all([
    PushNotifications.addListener('registration', t => { void sendToken(t.value); }),
    PushNotifications.addListener('registrationError', e => console.warn('[push] registreringen misslyckades', e.error)),
    // Kallstart: pluginet håller kvar trycket tills någon lyssnar.
    PushNotifications.addListener('pushNotificationActionPerformed', a => {
      const target = pushTargetFromData(a.notification?.data);
      if (target) window.dispatchEvent(new CustomEvent<PushTarget>(PUSH_OPEN_EVENT, { detail: target }));
    }),
    CapApp.addListener('appStateChange', s => { appActive = s.isActive; void reportPresence(); }),
  ]);
}

/**
 * Vid start och inloggning. Frågar inte om tillstånd — registrerar bara om användaren redan sagt ja.
 * email = null vid utloggning.
 */
export async function initPush(email: string | null): Promise<void> {
  if (!(await pushUsable())) return;
  try {
    await addListeners();
    if (!email) return;
    if (pendingToken) await sendToken(pendingToken);
    const p = await PushNotifications.checkPermissions();
    if (p.receive === 'granted') await PushNotifications.register();
  } catch (e) {
    console.warn('[push] start', e);
  }
}

/**
 * Frågar om tillstånd vid ett bra tillfälle (iOS visar frågan bara en gång, Android 13+ högst två) och registrerar
 * telefonen. Returnerar om notiser är på.
 */
export async function askForPush(): Promise<boolean> {
  if (!loggedIn() || !(await pushUsable())) return false;
  try {
    await addListeners();
    let p = await PushNotifications.checkPermissions();
    if (p.receive === 'prompt' || p.receive === 'prompt-with-rationale') p = await PushNotifications.requestPermissions();
    if (p.receive !== 'granted') return false;
    await PushNotifications.register();
    return true;
  } catch (e) {
    console.warn('[push] tillstånd', e);
    return false;
  }
}

/** Tillståndet utan att fråga: granted | denied | prompt | unavailable. */
export async function pushPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unavailable'> {
  if (!(await pushUsable())) return 'unavailable';
  try {
    const p = await PushNotifications.checkPermissions();
    return p.receive === 'granted' ? 'granted' : p.receive === 'denied' ? 'denied' : 'prompt';
  } catch {
    return 'unavailable';
  }
}

/** Vid utloggning: sluta skicka push till telefonen (görs innan inloggningen glöms). */
export async function forgetPushDevice(): Promise<void> {
  const token = storedToken();
  if (!token || !isPushAvailable()) return;
  try { await api.unregisterPushDevice(token); } catch { /* utloggad ändå */ }
  storeToken(null);
}

// ─── Öppen chatt ──────────────────────────────────────────────────

let openChat: string | null = null;
let appActive = true;
let heartbeat: ReturnType<typeof setInterval> | null = null;
let lastSent: string | null | undefined;

async function reportPresence(): Promise<void> {
  const token = storedToken();
  if (!token || !loggedIn() || !isPushAvailable()) return;
  const chat = appActive ? openChat : null;
  if (chat === lastSent && chat === null) return;
  lastSent = chat;
  try { await api.setPushPresence(token, chat); } catch { /* nästa signal rättar */ }
}

/**
 * Chatten som visas just nu: mark:<markId> (gruppchatten) eller hunt:<meetingId> (jaktchatten),
 * null när den stängs. API:t räknar en öppen chatt som stängd efter 90 s utan ny signal.
 */
export function setOpenChat(chat: string | null): void {
  if (chat === openChat) return;
  openChat = chat;
  if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
  if (chat) heartbeat = setInterval(() => { void reportPresence(); }, 60_000);
  void reportPresence();
}
